import React, { useState } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Sector } from 'recharts';
import {
  formatCurrencyAmount,
  formatNumberValue
} from '../../../../../utils/miscFunctions';
import { useSelector } from 'react-redux';
import { getSelectedCountry } from '../../../../../states/slices/resourceSlice';


const ActiveShape: React.FC<{
  cx?: number;
  cy?: number;
  innerRadius: number;
  outerRadius: number;
  startAngle: number;
  endAngle: number;
  fill: string;
}> = ({ cx = 0, cy = 0, innerRadius, outerRadius, startAngle, endAngle, fill }) => (
  <g>
    <Sector
      cx={cx}
      cy={cy}
      innerRadius={innerRadius}
      outerRadius={outerRadius + 6}
      startAngle={startAngle}
      endAngle={endAngle}
      fill={fill}
    />
    <Sector
      cx={cx}
      cy={cy}
      startAngle={startAngle}
      endAngle={endAngle}
      innerRadius={outerRadius + 8}
      outerRadius={outerRadius + 10}
      fill={fill}
    />
  </g>
);

const MotorPolicyDistributionChart = ({ motorPolicyDistributionData, isLoading, isRangeSelected }: MotorPolicyDistributionChartProps) => {
  const [activeIndex, setActiveIndex] = React.useState<number | null>(null);
  const country = useSelector(getSelectedCountry)
  const selectedCountryIso = country.currencyIso

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white dark:bg-gray-900 p-4 border border-gray-200 dark:border-gray-700 shadow-md rounded-md">
          <p className="font-medium text-gray-900 dark:text-white">{data.type}</p>
          <p className="text-sm text-gray-600 dark:text-white">Count: {formatNumberValue(data.count)}</p>
          <p className="text-sm text-gray-600 dark:text-white">Value: {formatCurrencyAmount(data.value)}</p>
          <p className="text-sm text-gray-600 dark:text-white">Share: {data.percentage?.toFixed(2) || '0.0'}%</p>
        </div>
      );
    }
    return null;
  };

  // Color palette for the chart segments (lighter shades)
  const COLORS = [
    '#a5b4fc', // indigo-300 (lighter shade)
    '#34d399', // emerald-400
    '#fbbf24', // amber-400
    '#f87171', // red-400
    '#a78bfa', // violet-400
    '#22d3ee', // cyan-400
    '#fb923c', // orange-400
    '#f472b6' // pink-400
  ];

  // Transform the data for the chart
  const chartData = React.useMemo(
    () =>
      motorPolicyDistributionData && motorPolicyDistributionData?.coverTypes?.map((type: any, index: number) => ({
        type,
        count: motorPolicyDistributionData?.counts[index] || 0,
        value: motorPolicyDistributionData?.amounts?.[index] || 0,
        color: COLORS[index % COLORS.length],
        percentage:
          motorPolicyDistributionData?.counts?.length > 0
            ? (motorPolicyDistributionData?.counts?.[index] /
              motorPolicyDistributionData?.counts?.reduce((a: number, b: number) => a + b, 0)) *
            100
            : 0
      })),
    [
      motorPolicyDistributionData?.coverTypes,
      motorPolicyDistributionData?.counts,
      motorPolicyDistributionData?.amounts
    ]
  );

  const safeChartData = chartData || [];
  const hasData = safeChartData.length > 0 && safeChartData.some((item: any) => item.count > 0);

  // Single source of truth for the total so the summary, percentages and the
  // merged comprehensive slice all agree.
  const totalCount = safeChartData.reduce((sum: number, item: any) => sum + (item.count || 0), 0);

  const comprehensiveItems = safeChartData.filter((data: any) => data.type?.includes('Comprehensive'));
  const nonComprehensiveData = safeChartData.filter((data: any) => !data.type?.includes('Comprehensive'));

  const sumOfPremiumComprehensive = comprehensiveItems.reduce((sum: number, item: any) => sum + (item.value || 0), 0);
  const sumOfCountComprehensive = comprehensiveItems.reduce((sum: number, item: any) => sum + (item.count || 0), 0);

  const comprehensiveData = {
    type: 'Comprehensive',
    count: sumOfCountComprehensive,
    value: sumOfPremiumComprehensive,
    percentage: totalCount > 0 ? (sumOfCountComprehensive / totalCount) * 100 : 0,
    color: '#a78bfa'
  };

  // Only surface the merged "Comprehensive" slice when such policies actually
  // exist; otherwise we'd render a phantom "Comprehensive: 0" legend entry/slice.
  const finalChartData = comprehensiveItems.length > 0
    ? [...nonComprehensiveData, comprehensiveData]
    : nonComprehensiveData;

  // Loading state
  if (isLoading) {
    return (
      <div className="h-full flex flex-col">
        <div className="flex justify-between items-center mb-6">
          <div>
            <div className="h-6 bg-gray-200 rounded w-48 animate-pulse mb-2"></div>
            <div className="h-4 bg-gray-100 rounded w-32 animate-pulse"></div>
          </div>
          <div className="w-32 h-8 bg-gray-100 rounded-lg animate-pulse"></div>
        </div>

        <div className="flex-1 flex flex-col">
          <div className=" mb-4 flex items-center justify-center">
            <div className="relative">
              <div className="w-32 h-32 border-8 border-gray-200 rounded-full animate-pulse"></div>
              <div className="absolute inset-4 w-24 h-24 border-4 border-gray-100 rounded-full animate-pulse"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full animate-pulse"></div>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-4 mb-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="flex items-center">
                <div className="w-3 h-3 bg-gray-200 rounded-full mr-2 animate-pulse"></div>
                <div className="h-3 bg-gray-100 rounded w-16 animate-pulse"></div>
              </div>
            ))}
          </div>

          <div className="mt-auto pt-4 border-t border-gray-200 dark:border-gray-700 grid grid-cols-3 gap-4">
            <div className="px-0 py-3 bg-gray-100 rounded-lg col-span-1 animate-pulse">
              <div className="h-3 bg-gray-200 rounded w-20 mx-auto mb-1"></div>
              <div className="h-5 bg-gray-300 rounded w-12 mx-auto"></div>
            </div>
            <div className="px-0 py-4 bg-gray-100 rounded-lg col-span-2 animate-pulse">
              <div className="h-3 bg-gray-200 rounded w-24 mx-auto mb-1"></div>
              <div className="h-5 bg-gray-300 rounded w-16 mx-auto"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Empty state
  if (!hasData) {
    return (
      <div className="h-full flex flex-col">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center max-w-md">
            <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-blue-50 to-indigo-100 rounded-full flex items-center justify-center">
              <svg className="w-10 h-10 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h4 className="text-lg font-semibold text-gray-900 dark:text-darkBg-mainText mb-2">
              No Motor Policies Found
            </h4>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
              There are no motor policy records available for the selected date range.
              Try selecting a different date range or check back later for updates.
            </p>
            <div className="inline-flex items-center px-4 py-2 bg-blue-50 text-blue-700 rounded-lg text-sm">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Try a different date range
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 flex flex-col">
        <div className="h-64 mb-4">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                activeShape={ActiveShape as any}
                data={finalChartData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={80}
                paddingAngle={2}
                dataKey="count"
                onMouseEnter={(_, index) => setActiveIndex(index)}
                onMouseLeave={() => setActiveIndex(null)}>
                {finalChartData.map((entry: any, index: number) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={entry.color}
                    fillOpacity={0.8}
                    stroke="#fff"
                    strokeWidth={1}
                  />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-4 mb-4">
          {finalChartData.map((entry: any, index: number) => (
            <div key={`legend-${index}`} className="flex items-center">
              <span
                className="w-3 h-3 rounded-full mr-2"
                style={{ backgroundColor: entry.color }}>
              </span>
              <span className="text-xs text-gray-600 dark:text-darkBg-mainText">
                {entry.type}: {formatNumberValue(entry.count)}
              </span>
            </div>
          ))}
        </div>

        <div className="mt-auto pt-4 border-t border-gray-200 dark:border-gray-700 grid grid-cols-3 gap-4 text-center">
          <div className="px-0 py-3 bg-gray-50 dark:bg-gray-800 rounded-lg col-span-1">
            <p className="text-xs font-medium text-gray-600 dark:text-gray-400">{`${isRangeSelected ? 'Policies' : 'YTD Policies'}`}</p>
            <p className="text-lg font-bold text-gray-900 dark:text-white">
              {formatNumberValue(totalCount)}
            </p>
          </div>
          <div className="px-0 py-4 bg-gray-50 dark:bg-gray-800 rounded-lg col-span-2">
            <p className="text-xs font-medium text-gray-600 dark:text-gray-400">{`${isRangeSelected ? 'Premium' : 'YTD Premium'} (${selectedCountryIso})`}</p>
            <p className="text-lg font-bold text-gray-900 dark:text-white">
              {formatCurrencyAmount(finalChartData.reduce((sum: number, item: any) => sum + item.value, 0), undefined, false)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MotorPolicyDistributionChart;
export interface PolicyDistributionData {
  coverTypes: string[];
  counts: number[];
  amounts: number[];
}
interface MotorPolicyDistributionChartProps {
  motorPolicyDistributionData?: PolicyDistributionData;
  isLoading: boolean;
  isRangeSelected: boolean;
}